// ONE REGISTRY of every sentence an owner can read in the consult web app
// (design 8.7: reviewed at the cutover checklist so no page promises what the
// consult does not do). Otto's spoken words are NOT here: they are versioned
// behavior data in @otto/config, edited in Otto Studio.
//
// House rules, checked by test/strings.test.ts: no em or en dashes anywhere.
//
// English: the approved Sept 13 2026 check-in copy (ottos-consult
// src/ConsultPage.jsx) plus the v2 additions, approved by Wes on Oct 14 2026
// (the intro rewritten for the one office direction, 15 minutes).
// Spanish: a v0 DRAFT by Claude, not reviewed by a native speaker (test T15,
// design 4.7). It only shows when the server lists "es" (SPANISH_ENABLED, off).
//
// Open copy questions for Wes (also in the build report):
//   - APPROVED by Wes Oct 14 2026 (was the REVIEW FIX of Sept 28 2026): the approved "Your
//     assessment arrives today" / "In your inbox today" / "Then he'll show you
//     what's possible" promised what this build does not do (the package
//     webhook is OFF by default and nothing sends an assessment), so the
//     check-in now says a person from our team follows up after the call. The
//     held state no longer promises a resume (can_resume is false until resume
//     links exist, design 8.6): heldDetailNoResume. Marked NEW (review) below.
//   - The voice-only line and the "person will reach out" route are NEW copy.

import type { Language } from "./language";

export interface ConsultStrings {
  site: {
    howItWorks: string;
    industries: string;
    pricing: string;
    about: string;
    faq: string;
    talkToUs: string;
    clientLogin: string;
    visitSite: string;
    homeLabel: string;
    brandAlt: string;
    badgeAlt: string;
    footerSummary: string;
    footerTagline: string;
    copyright: string;
    mainNav: string;
    footerNav: string;
  };
  banner: { free: string; done: string; saved: string };
  checkin: {
    mediaAlt: string;
    mediaStatus: string;
    titleLead: string;
    titleTail: string;
    introVideo: string;
    introVoice: string;
    stepsLabel: string;
    step1: string;
    step1Time: string;
    step2: string;
    step2Time: string;
    step3: string;
    step3Time: string;
    formBar: string;
    formBarNote: string;
    aboutYou: string;
    name: string;
    company: string;
    industry: string;
    industryPlaceholder: string;
    industryOther: string;
    industryOtherHint: string;
    email: string;
    emailHint: string;
    phone: string;
    phoneHint: string;
    howToTalk: string;
    modeVideo: string;
    modeVideoNote: string;
    modeVoice: string;
    modeVoiceNote: string;
    language: string;
    beforeYouStart: string;
    micLabel: string;
    micCopy: string;
    micTest: string;
    micTryAgain: string;
    micWaiting: string;
    micAllowPrompt: string;
    micListening: string;
    micGood: string;
    cameraLabel: string;
    cameraCopyVideo: string;
    cameraCopyVoice: string;
    consentLabel: string;
    /** CR-016: this browser's last consult dropped and can be picked back up. */
    resumeOffer: (firstName: string) => string;
    resumeOfferDetail: string;
    resumeOfferButton: string;
    privacyLink: string;
    start: string;
    starting: string;
    personLink: string;
    botCheckLabel: string;
  };
  errors: {
    required: string;
    email: string;
    phone: string;
    industryOther: string;
    consent: string;
    botCheck: string;
    botCheckUnavailable: string;
    network: string;
    rateLimited: string;
    paused: string;
    betweenCalls: string;
    bookedUp: string;
    atCapacity: string;
    modeUnavailable: string;
    languageUnavailable: string;
    generic: string;
    notOpen: string;
  };
  mic: {
    unavailable: string;
    denied: string;
    blockedBySystem: string;
    noDevice: string;
    inUse: string;
    iosSteps: string;
    androidSteps: string;
    desktopSteps: string;
    helpTitle: string;
    tryAgain: string;
  };
  inApp: {
    title: string;
    body: string;
    openChrome: string;
    iosSteps: string;
    copyLink: string;
    copied: string;
    continueAnyway: string;
  };
  person: {
    title: string;
    intro: string;
    how: string;
    email: string;
    text: string;
    call: string;
    send: string;
    sending: string;
    done: string;
    doneDetail: string;
    unavailable: string;
    back: string;
  };
  call: {
    headerTitle: string;
    headerStep: string;
    live: string;
    transcriptOn: string;
    ottoPill: string;
    listening: string;
    thinking: string;
    speaking: string;
    ready: string;
    you: string;
    cameraOff: string;
    micOn: string;
    micOff: string;
    canHear: string;
    muted: string;
    mute: string;
    unmute: string;
    captions: string;
    captionsOff: string;
    video: string;
    voice: string;
    switchToVoice: string;
    switchToVideo: string;
    retry3d: string;
    end: string;
    endShort: string;
    ending: string;
    endConfirm: string;
    endConfirmYes: string;
    endConfirmNo: string;
    unmuteOtto: string;
    reconnecting: string;
    serverRecovering: string;
    lost: string;
    rejoin: string;
    voiceOnlyDisclosure: string;
    keepScreenOpen: string;
    agendaTitle: string;
    clockSoFar: (minutes: number, target: number) => string;
    clockPastTarget: (minutes: number) => string;
    clockExtended: (minutes: number) => string;
    extensionAsk: string;
    extensionYes: string;
    extensionNo: string;
    extensionThanks: string;
    connectingTitle: string;
    connectingDetail: string;
    rejoinTitle: string;
    rejoinDetail: string;
    rejoinButton: string;
    rejoining: string;
    missingTitle: string;
    missingDetail: string;
    startNew: string;
    failedTitle: string;
    failedStatus: string;
    tryAgain: string;
    trying: string;
    alreadyOpen: string;
    expired: string;
    ended: string;
    unavailable: string;
    fullyBooked: string;
    genericFailure: string;
    connecting: string;
    keptPlace: string;
    didNotStart: string;
    avatarFallback: string;
  };
  thanks: {
    photoAlt: string;
    complete: string;
    saved: string;
    thankYou: (firstName: string) => string;
    thankYouNoName: string;
    allSet: string;
    toldOtto: (business: string) => string;
    saving: string;
    savingDetail: string;
    preparing: string;
    preparingDetail: string;
    ready: string;
    readyDetail: string;
    held: string;
    heldDetail: string;
    /** The held state when the consult cannot be resumed (StatusResponse.can_resume false). */
    heldDetailNoResume: string;
    resume: string;
    delayed: string;
    delayedDetail: string;
    person: string;
    personDetail: string;
    withdrawn: string;
    withdrawnDetail: string;
    stillOpen: string;
    stillOpenDetail: string;
    backToCall: string;
    notStarted: string;
    notStartedDetail: string;
    cannotRead: string;
    whatOttoHeard: string;
    ratingQuestion: string;
    ratingLabel: (score: number) => string;
    ratingComment: string;
    ratingSend: string;
    ratingThanks: string;
    ratingFailed: string;
    callback: string;
    visitSite: string;
    startAnother: string;
    englishReport: string;
  };
}

const en: ConsultStrings = {
  site: {
    howItWorks: "How it works",
    industries: "Industries",
    pricing: "Pricing",
    about: "About",
    faq: "FAQ",
    talkToUs: "Talk to us",
    clientLogin: "Client login",
    visitSite: "Visit ottosoffice.ai",
    homeLabel: "Otto's Office home",
    brandAlt: "Otto's Office",
    badgeAlt: "Otto's Office badge",
    footerSummary:
      "Otto's Office finds the work worth automating in your business and staffs it with a team you'll actually enjoy working with.",
    footerTagline: "Made by humans and robots who like their jobs.",
    copyright: "© 2026 OTTO'S OFFICE",
    mainNav: "Main navigation",
    footerNav: "Footer navigation",
  },
  banner: {
    free: "FREE CONSULT",
    done: "STEP 3 OF 3 · DONE",
    saved: "STEP 3 OF 3 · YOUR CONSULT IS SAVED",
  },
  checkin: {
    mediaAlt: "Otto at his desk, ready for the call",
    mediaStatus: "OTTO IS AT HIS DESK",
    titleLead: "See what's possible",
    titleTail: "for your business.",
    // Approved by Wes Oct 18 2026: the hub emails the assessment shortly after the call (was, Oct 14: a person follows up).
    introVideo:
      "You're about to get on a video call with Otto, the same as any Zoom you've been on. He'll ask how your business runs and what's holding it back: how you win customers, take care of them, get the work done and get paid. After the call, your assessment arrives by email shortly after: your whole office, and what each specialist would do for your business.",
    // Approved by Wes Oct 14 2026: the same intro for voice only.
    introVoice:
      "You're about to talk with Otto, voice only, like a phone call. He'll ask how your business runs and what's holding it back: how you win customers, take care of them, get the work done and get paid. After the call, your assessment arrives by email shortly after: your whole office, and what each specialist would do for your business.",
    stepsLabel: "Consult steps",
    step1: "Check in on the form below.",
    step1Time: "About a minute",
    step2: "Tell Otto about your business. No prep needed.",
    // Approved Oct 14 2026: the consult's 15 minute target.
    step2Time: "About 15 minutes",
    // Approved by Wes Oct 18 2026, "shortly after your call" (Oct 14: "Hear from our team about your assessment.").
    step3: "Get your assessment by email, shortly after your call.",
    step3Time: "After your call",
    formBar: "CHECK IN",
    formBarNote: "TAKES ABOUT A MINUTE",
    aboutYou: "A LITTLE ABOUT YOU",
    name: "YOUR NAME",
    company: "COMPANY NAME",
    industry: "INDUSTRY",
    industryPlaceholder: "For example: plumbing, dental office, nonprofit",
    industryOther: "YOUR INDUSTRY",
    industryOtherHint: "In a few words, for example: dog grooming.",
    email: "EMAIL",
    emailHint: "This is where your assessment goes.",
    phone: "PHONE",
    phoneHint: "So our team can reach you about your assessment.",
    howToTalk: "HOW DO YOU WANT TO TALK?",
    modeVideo: "Talk with Otto on video",
    modeVideoNote: "See Otto while you talk. Your camera stays off.",
    modeVoice: "Just talk, visual off",
    modeVoiceNote: "Voice only: same Otto, no video. Best somewhere quiet.",
    language: "LANGUAGE",
    beforeYouStart: "BEFORE YOU START",
    micLabel: "CHECK YOUR MICROPHONE",
    micCopy: "Give it a quick test right here. You'll be talking out loud with Otto, so pick somewhere quiet.",
    micTest: "Test my mic",
    micTryAgain: "Try the mic again",
    micWaiting: "Waiting for the mic...",
    micAllowPrompt: "Tap Allow when your browser asks.",
    micListening: "Listening. Say a few words.",
    micGood: "Sounds good. Otto will hear you.",
    cameraLabel: "CAMERA OFF",
    cameraCopyVideo:
      "Otto's camera is on, so you can see who you're talking to. Yours stays off, and we don't use it at all.",
    cameraCopyVoice: "Nobody's camera is on. It's just your voice and Otto's.",
    consentLabel: "I AGREE",
    resumeOffer: (firstName) => `Welcome back, ${firstName}. Pick up where you left off?`,
    resumeOfferDetail: "Otto kept your place, so you won't answer anything twice. Or start fresh below.",
    resumeOfferButton: "Pick up where we left off",
    privacyLink: "Privacy",
    start: "Start my consult",
    starting: "Starting...",
    personLink: "Prefer not to talk? A person will reach out",
    botCheckLabel: "Quick check that you're a person",
  },
  errors: {
    required: "Enter your name, business email, and business name to continue.",
    email: "Enter a business email we can send your assessment to.",
    phone: "Please enter a full phone number, like (210) 555-0142.",
    industryOther: "Tell us your industry in a few words.",
    consent: "Check the box to agree before you start.",
    botCheck: "Finish the quick check above, then tap Start my consult again.",
    botCheckUnavailable: "Our quick check did not load. Refresh the page to try again.",
    network: "We could not reach Otto's Office. Check your connection and try again.",
    rateLimited: "Too many tries from here for now. Please try again a little later.",
    paused: "Otto isn't taking consults right now. Leave your details and a person will reach out.",
    betweenCalls: "Otto is between calls. Please try again in a few minutes.",
    bookedUp: "Otto's booked up for today. Leave your details and a person will reach out.",
    atCapacity: "Otto is fully booked right now. Please try again in a few minutes.",
    modeUnavailable: "That way of talking isn't available right now. Please pick the other one.",
    languageUnavailable: "That language isn't available yet. Please pick English.",
    generic: "We could not start your consult. Please try again.",
    notOpen: "Check-in isn't open right now. Leave your details and a person will reach out.",
  },
  mic: {
    unavailable: "This browser can't use a microphone. Open this page in Safari or Chrome to talk with Otto.",
    denied: "Allow microphone access to talk with Otto.",
    blockedBySystem:
      "Your device is blocking the microphone for this browser. Turn it on in your device settings, then try again.",
    noDevice: "We can't find a microphone. Plug one in or switch devices, then try again.",
    inUse: "Another app is using your microphone. Close it, then try again.",
    iosSteps: "On an iPhone: tap the aA button in the address bar, then Website Settings, and set Microphone to Allow.",
    androidSteps: "On Android: tap the lock next to the address, then Permissions, and turn on Microphone.",
    desktopSteps:
      "On a computer: click the lock or settings icon next to the address and allow the microphone for this site.",
    helpTitle: "Otto needs your microphone",
    tryAgain: "Try again",
  },
  inApp: {
    title: "Open this page in your browser",
    body: "You're inside another app's browser, where the microphone may not work. For the best call, open this page in Safari or Chrome.",
    openChrome: "Open in Chrome",
    iosSteps: "Tap the menu in this app, then Open in browser or Open in Safari.",
    copyLink: "Copy the link",
    copied: "Link copied. Paste it into Safari or Chrome.",
    continueAnyway: "Continue here anyway",
  },
  person: {
    title: "Prefer not to talk?",
    intro:
      "Fill in the same details and tell us how to reach you. A person from our team will reach out by the next business day.",
    how: "HOW SHOULD WE REACH YOU?",
    email: "Email",
    text: "Text",
    call: "Phone call",
    send: "Ask a person to reach out",
    sending: "Sending...",
    done: "Thanks. A person will reach out.",
    doneDetail: "Someone from our team will get in touch by the next business day.",
    unavailable: "We could not send that just now. You can reach our team directly here:",
    back: "Back to talking with Otto",
  },
  call: {
    headerTitle: "YOUR FREE CONSULT",
    headerStep: "STEP 2 OF 3",
    live: "LIVE",
    transcriptOn: "TRANSCRIPT ON",
    ottoPill: "OTTO · MANAGER",
    listening: "LISTENING",
    thinking: "THINKING",
    speaking: "SPEAKING",
    ready: "HERE",
    you: "YOU",
    // Wes, Oct 14 2026: was "CAMERA NOT NEEDED".
    cameraOff: "CAMERA OFF",
    micOn: "MIC ON",
    micOff: "MIC OFF",
    canHear: "OTTO CAN HEAR YOU",
    muted: "MIC IS MUTED",
    mute: "MUTE",
    unmute: "UNMUTE",
    captions: "CAPTIONS",
    captionsOff: "Captions",
    video: "VIDEO",
    voice: "VOICE ONLY",
    switchToVoice: "Switch to voice only",
    switchToVideo: "Switch to video",
    retry3d: "Show the 3D Otto again",
    end: "End call",
    endShort: "End",
    ending: "Ending...",
    endConfirm: "End the call now? Otto will wrap up.",
    endConfirmYes: "Yes, end it",
    endConfirmNo: "Keep talking",
    unmuteOtto: "Tap to unmute Otto",
    reconnecting: "Reconnecting to Otto",
    serverRecovering: "One moment, Otto is getting back on the line",
    lost: "Your connection dropped. Otto kept your place.",
    rejoin: "Rejoin",
    // NEW (Sept 28, design 9.2): the written AI disclosure on the voice-only screen.
    voiceOnlyDisclosure: "You're talking with Otto, Otto's Office's AI manager.",
    keepScreenOpen: "Keep this screen open during the call.",
    // Wes, Oct 14 2026: was "WHERE WE ARE".
    agendaTitle: "CONSULT AGENDA",
    // Wes, Oct 14 2026: "estimated", not "planned".
    clockSoFar: (minutes, target) => `${minutes} min so far, about ${target} estimated`,
    clockPastTarget: (minutes) => `${minutes} min so far, wrapping up soon`,
    clockExtended: (minutes) => `${minutes} min so far. Thanks for the extra time.`,
    extensionAsk: "Otto asked if you have a few more minutes. Answer out loud, or tap:",
    extensionYes: "Yes, keep going",
    extensionNo: "Let's wrap up",
    extensionThanks: "Got it.",
    connectingTitle: "Otto is picking up...",
    connectingDetail: "Just a moment while he joins the call.",
    rejoinTitle: "Rejoin your call with Otto.",
    rejoinDetail: "Tap below to turn audio back on and pick the conversation up where you left off.",
    rejoinButton: "Rejoin call",
    rejoining: "Rejoining...",
    missingTitle: "We can't find this call here.",
    missingDetail:
      "This call was started in another browser or tab, or its link has expired. You can start a new consult in a minute.",
    startNew: "Start a new consult",
    failedTitle: "We couldn't reach Otto.",
    failedStatus: "CALL DIDN'T START",
    tryAgain: "Try again",
    trying: "Trying again...",
    alreadyOpen: "This call is already open in another tab or device. Close it there, then try again here.",
    expired: "This call link has expired. Start a new consult in a minute.",
    ended: "This call has ended.",
    unavailable: "Otto can't take the call right now. Please try again in a few minutes.",
    fullyBooked: "Otto is fully booked right now. Please try again in a few minutes.",
    genericFailure: "Check your connection and try again.",
    connecting: "CONNECTING",
    keptPlace: "OTTO KEPT YOUR PLACE",
    didNotStart: "CALL DIDN'T START",
    avatarFallback: "Showing Otto's picture to keep the call smooth on this device.",
  },
  thanks: {
    photoAlt: "Otto at his desk",
    complete: "CONSULT COMPLETE",
    saved: "CONSULT SAVED",
    thankYou: (firstName) => `Thank you, ${firstName}!`,
    thankYouNoName: "Thank you!",
    allSet: "You're all set.",
    toldOtto: (business) => `Thanks for telling Otto all about ${business}.`,
    saving: "Saving your conversation",
    savingDetail: "Otto is putting his notes in order. This takes a moment.",
    preparing: "Your assessment is being prepared",
    // Approved by Wes Oct 14 2026 (was "Keep an eye out for an email from hello@ottosoffice.ai."; nothing sends one yet).
    preparingDetail: "Otto's notes are saved. A person from our team will follow up about your assessment.",
    ready: "Otto's notes are saved",
    readyDetail: "Everything you told Otto is in. A person from our team will follow up about your assessment.",
    held: "We need a few more minutes with you",
    heldDetail: "Pick up where you left off whenever you're ready.",
    // Approved by Wes Oct 14 2026: the held state while nothing can be resumed (no resume link or email yet, design 8.6).
    heldDetailNoResume: "Your conversation is saved. When you have a few more minutes, you're welcome to start a new consult.",
    resume: "Pick up where we left off",
    delayed: "It's taking longer than usual",
    delayedDetail: "Our team is on it and will reach out by the next business day.",
    person: "A person will reach out",
    personDetail: "Someone from our team will get in touch by the next business day.",
    withdrawn: "Your conversation was not kept",
    withdrawnDetail: "As you asked, we deleted the transcript and sent nothing.",
    stillOpen: "Your call is still open",
    stillOpenDetail: "You can go back and finish talking with Otto.",
    backToCall: "Back to the call",
    notStarted: "This consult didn't start",
    notStartedDetail: "You can start a new one in a minute.",
    cannotRead: "We could not check on your consult just now. We'll keep trying.",
    whatOttoHeard: "Here's what Otto heard",
    ratingQuestion: "How did that feel?",
    ratingLabel: (score) => `${score} out of 5`,
    ratingComment: "What surprised you? (optional)",
    ratingSend: "Send",
    ratingThanks: "Thanks for telling us.",
    ratingFailed: "We could not save that just now. Please try again.",
    callback: "Talk to a person",
    visitSite: "Visit ottosoffice.ai",
    startAnother: "Start another consult",
    englishReport: "",
  },
};

// v0 DRAFT, not reviewed by a native speaker (design 4.7, test T15). "usted" register, neutral Latin American Spanish.
const es: ConsultStrings = {
  site: {
    howItWorks: "Cómo funciona",
    industries: "Industrias",
    pricing: "Precios",
    about: "Nosotros",
    faq: "Preguntas",
    talkToUs: "Hable con nosotros",
    clientLogin: "Acceso de clientes",
    visitSite: "Visite ottosoffice.ai",
    homeLabel: "Inicio de Otto's Office",
    brandAlt: "Otto's Office",
    badgeAlt: "Insignia de Otto's Office",
    footerSummary:
      "Otto's Office encuentra el trabajo que vale la pena automatizar en su negocio y lo pone en manos de un equipo con el que le va a gustar trabajar.",
    footerTagline: "Hecho por humanos y robots a los que les gusta su trabajo.",
    copyright: "© 2026 OTTO'S OFFICE",
    mainNav: "Navegación principal",
    footerNav: "Navegación del pie de página",
  },
  banner: {
    free: "CONSULTA GRATIS",
    done: "PASO 3 DE 3 · LISTO",
    saved: "PASO 3 DE 3 · SU CONSULTA ESTÁ GUARDADA",
  },
  checkin: {
    mediaAlt: "Otto en su escritorio, listo para la llamada",
    mediaStatus: "OTTO ESTÁ EN SU ESCRITORIO",
    titleLead: "Vea lo que es posible",
    titleTail: "para su negocio.",
    introVideo:
      "Está por tener una videollamada con Otto, como cualquier Zoom. Él le va a preguntar cómo funciona su negocio: qué vende, cómo se hace el trabajo, dónde se acumulan las cosas. Después de la llamada, su evaluación le llega por correo poco después.",
    introVoice:
      "Está por hablar con Otto, solo por voz, como una llamada telefónica. Él le va a preguntar cómo funciona su negocio: qué vende, cómo se hace el trabajo, dónde se acumulan las cosas. Después de la llamada, su evaluación le llega por correo poco después.",
    stepsLabel: "Pasos de la consulta",
    step1: "Regístrese en el formulario de abajo.",
    step1Time: "Como un minuto",
    step2: "Cuéntele a Otto sobre su negocio. No necesita preparar nada.",
    step2Time: "Como 15 minutos",
    step3: "Reciba su evaluación por correo, poco después de la llamada.",
    step3Time: "Después de la llamada",
    formBar: "REGISTRO",
    formBarNote: "TOMA COMO UN MINUTO",
    aboutYou: "UN POCO SOBRE USTED",
    name: "SU NOMBRE",
    company: "NOMBRE DE LA EMPRESA",
    industry: "INDUSTRIA",
    industryPlaceholder: "Por ejemplo: plomería, consultorio dental, organización sin fines de lucro",
    industryOther: "SU INDUSTRIA",
    industryOtherHint: "En pocas palabras, por ejemplo: estética canina.",
    email: "CORREO",
    emailHint: "Aquí le enviamos su evaluación.",
    phone: "TELÉFONO",
    phoneHint: "Para que nuestro equipo pueda comunicarse con usted.",
    howToTalk: "¿CÓMO QUIERE HABLAR?",
    modeVideo: "Hablar con Otto por video",
    modeVideoNote: "Vea a Otto mientras habla. Su cámara queda apagada.",
    modeVoice: "Solo voz, sin video",
    modeVoiceNote: "Solo voz: el mismo Otto, sin video. Mejor en un lugar tranquilo.",
    language: "IDIOMA",
    beforeYouStart: "ANTES DE EMPEZAR",
    micLabel: "PRUEBE SU MICRÓFONO",
    micCopy: "Hágale una prueba rápida aquí. Va a hablar en voz alta con Otto, así que busque un lugar tranquilo.",
    micTest: "Probar mi micrófono",
    micTryAgain: "Probar de nuevo",
    micWaiting: "Esperando el micrófono...",
    micAllowPrompt: "Toque Permitir cuando su navegador pregunte.",
    micListening: "Escuchando. Diga unas palabras.",
    micGood: "Se escucha bien. Otto lo va a oír.",
    cameraLabel: "CÁMARA APAGADA",
    cameraCopyVideo:
      "La cámara de Otto está encendida para que vea con quién habla. La suya queda apagada y no la usamos.",
    cameraCopyVoice: "Ninguna cámara está encendida. Solo su voz y la de Otto.",
    consentLabel: "ACEPTO",
    resumeOffer: (firstName) => `Qué gusto verle de nuevo, ${firstName}. ¿Seguimos donde nos quedamos?`,
    resumeOfferDetail: "Otto guardó su lugar, así que no tendrá que repetir nada. O empiece de nuevo abajo.",
    resumeOfferButton: "Seguir donde nos quedamos",
    privacyLink: "Privacidad",
    start: "Empezar mi consulta",
    starting: "Empezando...",
    personLink: "¿Prefiere no hablar? Una persona se comunicará con usted",
    botCheckLabel: "Verificación rápida de que es una persona",
  },
  errors: {
    required: "Escriba su nombre, su correo de trabajo y el nombre de su negocio para continuar.",
    email: "Escriba un correo de trabajo donde podamos enviarle su evaluación.",
    phone: "Escriba un número de teléfono completo, por ejemplo (210) 555-0142.",
    industryOther: "Cuéntenos su industria en pocas palabras.",
    consent: "Marque la casilla para aceptar antes de empezar.",
    botCheck: "Complete la verificación de arriba y toque Empezar mi consulta otra vez.",
    botCheckUnavailable: "La verificación no cargó. Actualice la página para intentarlo de nuevo.",
    network: "No pudimos comunicarnos con Otto's Office. Revise su conexión e inténtelo de nuevo.",
    rateLimited: "Demasiados intentos desde aquí por ahora. Inténtelo un poco más tarde.",
    paused: "Otto no está tomando consultas ahora. Deje sus datos y una persona se comunicará con usted.",
    betweenCalls: "Otto está entre llamadas. Inténtelo en unos minutos.",
    bookedUp: "Otto tiene la agenda llena por hoy. Deje sus datos y una persona se comunicará con usted.",
    atCapacity: "Otto está ocupado en este momento. Inténtelo en unos minutos.",
    modeUnavailable: "Esa forma de hablar no está disponible ahora. Elija la otra.",
    languageUnavailable: "Ese idioma todavía no está disponible. Elija inglés.",
    generic: "No pudimos empezar su consulta. Inténtelo de nuevo.",
    notOpen: "El registro no está abierto ahora. Deje sus datos y una persona se comunicará con usted.",
  },
  mic: {
    unavailable: "Este navegador no puede usar un micrófono. Abra esta página en Safari o Chrome para hablar con Otto.",
    denied: "Permita el acceso al micrófono para hablar con Otto.",
    blockedBySystem:
      "Su dispositivo está bloqueando el micrófono para este navegador. Actívelo en la configuración e inténtelo de nuevo.",
    noDevice: "No encontramos un micrófono. Conecte uno o cambie de dispositivo e inténtelo de nuevo.",
    inUse: "Otra aplicación está usando su micrófono. Ciérrela e inténtelo de nuevo.",
    iosSteps:
      "En un iPhone: toque el botón aA en la barra de direcciones, luego Configuración del sitio web, y ponga Micrófono en Permitir.",
    androidSteps: "En Android: toque el candado junto a la dirección, luego Permisos, y active Micrófono.",
    desktopSteps:
      "En una computadora: haga clic en el candado junto a la dirección y permita el micrófono para este sitio.",
    helpTitle: "Otto necesita su micrófono",
    tryAgain: "Intentar de nuevo",
  },
  inApp: {
    title: "Abra esta página en su navegador",
    body: "Está dentro del navegador de otra aplicación, donde el micrófono puede no funcionar. Para una mejor llamada, abra esta página en Safari o Chrome.",
    openChrome: "Abrir en Chrome",
    iosSteps: "Toque el menú de esta aplicación y luego Abrir en el navegador o Abrir en Safari.",
    copyLink: "Copiar el enlace",
    copied: "Enlace copiado. Péguelo en Safari o Chrome.",
    continueAnyway: "Seguir aquí de todos modos",
  },
  person: {
    title: "¿Prefiere no hablar?",
    intro:
      "Complete los mismos datos y díganos cómo comunicarnos. Una persona de nuestro equipo se comunicará con usted el siguiente día hábil.",
    how: "¿CÓMO NOS COMUNICAMOS CON USTED?",
    email: "Correo",
    text: "Mensaje de texto",
    call: "Llamada",
    send: "Pedir que una persona me contacte",
    sending: "Enviando...",
    done: "Gracias. Una persona se comunicará con usted.",
    doneDetail: "Alguien de nuestro equipo se pondrá en contacto el siguiente día hábil.",
    unavailable: "No pudimos enviarlo ahora. Puede comunicarse con nuestro equipo aquí:",
    back: "Volver a hablar con Otto",
  },
  call: {
    headerTitle: "SU CONSULTA GRATIS",
    headerStep: "PASO 2 DE 3",
    live: "EN VIVO",
    transcriptOn: "TRANSCRIPCIÓN ACTIVA",
    ottoPill: "OTTO · GERENTE",
    listening: "ESCUCHANDO",
    thinking: "PENSANDO",
    speaking: "HABLANDO",
    ready: "AQUÍ",
    you: "USTED",
    cameraOff: "CÁMARA APAGADA",
    micOn: "MICRÓFONO ACTIVO",
    micOff: "MICRÓFONO APAGADO",
    canHear: "OTTO LO ESCUCHA",
    muted: "MICRÓFONO SILENCIADO",
    mute: "SILENCIAR",
    unmute: "ACTIVAR",
    captions: "SUBTÍTULOS",
    captionsOff: "Subtítulos",
    video: "VIDEO",
    voice: "SOLO VOZ",
    switchToVoice: "Cambiar a solo voz",
    switchToVideo: "Cambiar a video",
    retry3d: "Mostrar otra vez a Otto en 3D",
    end: "Terminar llamada",
    endShort: "Terminar",
    ending: "Terminando...",
    endConfirm: "¿Terminar la llamada ahora? Otto va a cerrar.",
    endConfirmYes: "Sí, terminar",
    endConfirmNo: "Seguir hablando",
    unmuteOtto: "Toque para escuchar a Otto",
    reconnecting: "Reconectando con Otto",
    serverRecovering: "Un momento, Otto está volviendo a la línea",
    lost: "Se cortó su conexión. Otto guardó su lugar.",
    rejoin: "Volver",
    voiceOnlyDisclosure: "Está hablando con Otto, el gerente de inteligencia artificial de Otto's Office.",
    keepScreenOpen: "Mantenga esta pantalla abierta durante la llamada.",
    agendaTitle: "AGENDA DE LA CONSULTA",
    clockSoFar: (minutes, target) => `${minutes} min hasta ahora, unos ${target} estimados`,
    clockPastTarget: (minutes) => `${minutes} min hasta ahora, ya casi terminamos`,
    clockExtended: (minutes) => `${minutes} min hasta ahora. Gracias por el tiempo extra.`,
    extensionAsk: "Otto le preguntó si tiene unos minutos más. Responda en voz alta, o toque:",
    extensionYes: "Sí, sigamos",
    extensionNo: "Terminemos",
    extensionThanks: "Entendido.",
    connectingTitle: "Otto está contestando...",
    connectingDetail: "Un momento mientras se une a la llamada.",
    rejoinTitle: "Vuelva a su llamada con Otto.",
    rejoinDetail: "Toque abajo para activar el audio y seguir donde se quedaron.",
    rejoinButton: "Volver a la llamada",
    rejoining: "Volviendo...",
    missingTitle: "No encontramos esta llamada aquí.",
    missingDetail:
      "Esta llamada empezó en otro navegador o pestaña, o su enlace venció. Puede empezar una consulta nueva en un minuto.",
    startNew: "Empezar una consulta nueva",
    failedTitle: "No pudimos comunicarnos con Otto.",
    failedStatus: "LA LLAMADA NO EMPEZÓ",
    tryAgain: "Intentar de nuevo",
    trying: "Intentando de nuevo...",
    alreadyOpen: "Esta llamada ya está abierta en otra pestaña o dispositivo. Ciérrela allá e inténtelo aquí.",
    expired: "El enlace de esta llamada venció. Empiece una consulta nueva en un minuto.",
    ended: "Esta llamada terminó.",
    unavailable: "Otto no puede atender ahora. Inténtelo en unos minutos.",
    fullyBooked: "Otto está ocupado en este momento. Inténtelo en unos minutos.",
    genericFailure: "Revise su conexión e inténtelo de nuevo.",
    connecting: "CONECTANDO",
    keptPlace: "OTTO GUARDÓ SU LUGAR",
    didNotStart: "LA LLAMADA NO EMPEZÓ",
    avatarFallback: "Mostramos la imagen de Otto para que la llamada vaya fluida en este dispositivo.",
  },
  thanks: {
    photoAlt: "Otto en su escritorio",
    complete: "CONSULTA COMPLETA",
    saved: "CONSULTA GUARDADA",
    thankYou: (firstName) => `¡Gracias, ${firstName}!`,
    thankYouNoName: "¡Gracias!",
    allSet: "Todo listo.",
    toldOtto: (business) => `Gracias por contarle a Otto todo sobre ${business}.`,
    saving: "Guardando su conversación",
    savingDetail: "Otto está ordenando sus notas. Toma un momento.",
    preparing: "Estamos preparando su evaluación",
    preparingDetail: "Las notas de Otto están guardadas. Una persona de nuestro equipo le dará seguimiento sobre su evaluación.",
    ready: "Las notas de Otto están guardadas",
    readyDetail:
      "Todo lo que le contó a Otto quedó registrado. Una persona de nuestro equipo le dará seguimiento sobre su evaluación.",
    held: "Necesitamos unos minutos más con usted",
    heldDetail: "Siga donde se quedó cuando esté listo.",
    heldDetailNoResume: "Su conversación está guardada. Cuando tenga unos minutos más, puede empezar una nueva consulta.",
    resume: "Seguir donde nos quedamos",
    delayed: "Está tardando más de lo normal",
    delayedDetail: "Nuestro equipo está en eso y se comunicará con usted el siguiente día hábil.",
    person: "Una persona se comunicará con usted",
    personDetail: "Alguien de nuestro equipo se pondrá en contacto el siguiente día hábil.",
    withdrawn: "No guardamos su conversación",
    withdrawnDetail: "Como pidió, borramos la transcripción y no enviamos nada.",
    stillOpen: "Su llamada sigue abierta",
    stillOpenDetail: "Puede volver y terminar de hablar con Otto.",
    backToCall: "Volver a la llamada",
    notStarted: "Esta consulta no empezó",
    notStartedDetail: "Puede empezar una nueva en un minuto.",
    cannotRead: "No pudimos revisar su consulta en este momento. Seguimos intentando.",
    whatOttoHeard: "Esto es lo que Otto escuchó",
    ratingQuestion: "¿Cómo se sintió?",
    ratingLabel: (score) => `${score} de 5`,
    ratingComment: "¿Qué le sorprendió? (opcional)",
    ratingSend: "Enviar",
    ratingThanks: "Gracias por contárnoslo.",
    ratingFailed: "No pudimos guardarlo ahora. Inténtelo de nuevo.",
    callback: "Hablar con una persona",
    visitSite: "Visite ottosoffice.ai",
    startAnother: "Empezar otra consulta",
    // Design 4.7 (D10b): the engine writes English for now; Wes decides this wording.
    englishReport: "Por ahora, la evaluación escrita llega en inglés.",
  },
};

export const STRINGS: Record<Language, ConsultStrings> = { en, es };

export function stringsFor(language: Language): ConsultStrings {
  return STRINGS[language] ?? en;
}

/**
 * v0 DRAFT consent text, shown ONLY in local development when otto-app does not
 * send `consent_notice` (its setting is empty). A production build refuses to check
 * anyone in without the server's text, so no one agrees to words the server did
 * not define. Source: design 9.2's example; the English was approved by counsel on Oct 14 2026
 * (Wes). Production shows the server's text (the consent_notice setting), never this one.
 */
export const DEV_FALLBACK_CONSENT: Record<Language, string> = {
  en: "Otto is Otto's Office's AI manager, and he takes notes as we talk. I agree that Otto's Office may transcribe this conversation to prepare my assessment. We don't keep recordings of your voice; our AI providers process the audio to run the call. I'm 18 or older and here on behalf of a business.",
  es: "Otto es el gerente de inteligencia artificial de Otto's Office y toma notas mientras hablamos. Acepto que Otto's Office transcriba esta conversación para preparar mi evaluación. No guardamos grabaciones de su voz; nuestros proveedores de inteligencia artificial procesan el audio para hacer la llamada. Tengo 18 años o más y estoy aquí en nombre de un negocio.",
};

/** Public site links (the approved check-in look links to the marketing site). */
export const SITE = Object.freeze({
  home: "https://ottosoffice.ai/",
  howItWorks: "https://ottosoffice.ai/how-it-works",
  industries: "https://ottosoffice.ai/industries",
  pricing: "https://ottosoffice.ai/pricing",
  about: "https://ottosoffice.ai/about",
  faq: "https://ottosoffice.ai/faq",
  contact: "https://ottosoffice.ai/talk-to-us",
  clientLogin: "https://app.ottosoffice.ai",
  privacy: "https://ottosoffice.ai/privacy",
});
